import { optionalQueryParams } from "../apiEndpointHelpers";
import type { ApiEndpointBuilder } from "../apiEndpointHelpers";
import type {
  AdminV2CreateMessagingTemplateInput,
  AdminV2MessagingNewTemplatePreviewInput,
  AdminV2MessagingTemplateAssetMetadataInput,
  AdminV2MessagingTemplateAssetsQuery,
  AdminV2MessagingTemplateHtmlImportBudget,
  AdminV2MessagingTemplateHtmlImportFramePreviewInput,
  AdminV2MessagingTemplateHtmlImportFramePreviewResponse,
  AdminV2MessagingTemplateHtmlImportPreviewInput,
  AdminV2MessagingTemplateHtmlImportPreviewResponse,
  AdminV2MessagingTemplateImageUploadInput,
  AdminV2MessagingTemplateImageUploadResponse,
  AdminV2MessagingTemplatePreviewContactsResponse,
  AdminV2MessagingTemplatePreviewInput,
  AdminV2MessagingTemplateRestoreAsDraftInput,
  AdminV2MessagingTemplateRestoreAsDraftResponse,
  AdminV2MessagingTemplateVersionCompareInput,
  AdminV2MessagingTemplateVersionCompareResponse,
  AdminV2UpdateMessagingTemplateInput,
} from "./adminV2MessagingTemplateEndpointTypes";

import type {
  AdminV2MessageTemplateDetail,
  AdminV2MessageTemplateLibraryCategory,
  AdminV2MessageTemplatePreviewResponse,
  AdminV2MessageTemplatesResponse,
  AdminV2MessageTemplateStatus,
  AdminV2MessagingCategory,
  AdminV2MessagingChannel,
  AdminV2MessagingTemplateAsset,
  AdminV2MessagingTemplateAssetListResponse,
  AdminV2MessagingTemplateVersionHistoryResponse,
} from "@/types/adminV2";
import type {
  MessagingBuiltInCatalogue,
  MessagingBuiltInDetail,
  MessagingBuiltInId,
  MessagingBuiltInVariant,
} from "@/types/adminV2/messagingBuiltInTemplates";
import type { AdminV2MessagingTemplateSelfTestRecipientsResponse } from "@/types/adminV2/messagingTemplateSelfTest";

/** Owns the Admin template-library, rich-version, preview, and image requests. */
export const buildAdminV2MessagingTemplateEndpoints = (
  builder: ApiEndpointBuilder,
) => ({
  getAdminV2MessagingBuiltInTemplates: builder.query<
    MessagingBuiltInCatalogue,
    { ownerKey: string }
  >({
    query: () => ({ url: "/admin/v2/messaging/built-in-templates" }),
    keepUnusedDataFor: 0,
  }),
  getAdminV2MessagingBuiltInTemplate: builder.query<
    MessagingBuiltInDetail,
    {
      ownerKey: string;
      id: MessagingBuiltInId;
      variant: MessagingBuiltInVariant | null;
      expectedRevision?: string;
    }
  >({
    query: ({ id, variant, expectedRevision }) => ({
      url: `/admin/v2/messaging/built-in-templates/${encodeURIComponent(id)}`,
      params: {
        ...(variant ?? {}),
        ...(expectedRevision ? { expectedRevision } : {}),
      },
    }),
    keepUnusedDataFor: 0,
  }),
  getAdminV2MessagingTemplates: builder.query<
    AdminV2MessageTemplatesResponse,
    {
      ownerKey?: string;
      folderId?: string;
      organisationId?: string;
      globalOnly?: boolean;
      includeArchived?: boolean;
      includeLibrarySummary?: boolean;
      includeNotInUseCount?: boolean;
      includeUsageSummary?: boolean;
      notInUse?: boolean;
      /** EA-10: only templates the Email Assistant personal copy can use. */
      personalCopyEligible?: boolean;
      workflowUsage?: "used" | "not_used";
      q?: string;
      status?: AdminV2MessageTemplateStatus;
      channel?: AdminV2MessagingChannel;
      category?: AdminV2MessagingCategory;
      libraryCategory?: AdminV2MessageTemplateLibraryCategory;
      limit?: number;
      offset?: number;
    } | void
  >({
    query: (params) => {
      const wire = { ...(params ?? {}) };
      delete wire.ownerKey;
      return {
        url: "/admin/v2/messaging/templates",
        params: optionalQueryParams(wire),
      };
    },
    providesTags: [{ type: "AdminV2MessagingTemplates", id: "LIST" }],
    keepUnusedDataFor: 20,
  }),

  getAdminV2MessagingTemplate: builder.query<
    AdminV2MessageTemplateDetail,
    // Actor-bound consumers partition their cache; ownership never goes on the wire.
    { templateId: string; ownerKey?: string }
  >({
    query: ({ templateId }) => ({
      url: `/admin/v2/messaging/templates/${templateId}`,
    }),
    providesTags: (_result, _error, { templateId }) => [
      { type: "AdminV2MessagingTemplates", id: templateId },
    ],
    keepUnusedDataFor: 20,
  }),

  getAdminV2MessagingTemplateSelfTestRecipients: builder.query<
    AdminV2MessagingTemplateSelfTestRecipientsResponse,
    void | { permissionScope: string; suppressAccessDeniedModal?: boolean }
  >({
    query: (args) => ({
      url: "/admin/v2/messaging/templates/test-send/recipients",
      // This optional lookup can fail inside its own test dialog without
      // interrupting an unrelated workflow with global access guidance.
      ...(args?.suppressAccessDeniedModal
        ? { suppressAccessDeniedModal: true }
        : {}),
    }),
    keepUnusedDataFor: 20,
  }),

  getAdminV2MessagingTemplatePreviewContacts: builder.query<
    AdminV2MessagingTemplatePreviewContactsResponse,
    { q: string; organisationId?: string; limit?: number }
  >({
    query: (params) => ({
      url: "/admin/v2/messaging/templates/preview-contexts",
      params: optionalQueryParams(params),
    }),
    keepUnusedDataFor: 10,
  }),

  createAdminV2MessagingTemplate: builder.mutation<
    AdminV2MessageTemplateDetail,
    AdminV2CreateMessagingTemplateInput
  >({
    query: (body) => ({
      url: "/admin/v2/messaging/templates",
      method: "POST",
      data: body,
    }),
    invalidatesTags: [
      { type: "AdminV2MessagingTemplates", id: "LIST" },
      { type: "AdminV2AuditLog", id: "LIST" },
    ],
  }),

  updateAdminV2MessagingTemplate: builder.mutation<
    AdminV2MessageTemplateDetail,
    AdminV2UpdateMessagingTemplateInput
  >({
    query: ({ templateId, body }) => ({
      url: `/admin/v2/messaging/templates/${templateId}`,
      method: "PUT",
      data: body,
    }),
    invalidatesTags: (_result, _error, { templateId }) => [
      { type: "AdminV2MessagingTemplates", id: "LIST" },
      { type: "AdminV2MessagingTemplates", id: templateId },
      { type: "AdminV2AuditLog", id: "LIST" },
    ],
  }),

  publishAdminV2MessagingTemplate: builder.mutation<
    AdminV2MessageTemplateDetail,
    { templateId: string; expectedDraftVersionId: string }
  >({
    query: ({ templateId, expectedDraftVersionId }) => ({
      url: `/admin/v2/messaging/templates/${templateId}/publish`,
      method: "POST",
      data: { expectedDraftVersionId },
    }),
    invalidatesTags: (_result, _error, { templateId }) => [
      { type: "AdminV2MessagingTemplates", id: "LIST" },
      { type: "AdminV2MessagingTemplates", id: templateId },
      { type: "AdminV2AuditLog", id: "LIST" },
    ],
  }),

  unpublishAdminV2MessagingTemplate: builder.mutation<
    AdminV2MessageTemplateDetail,
    { templateId: string; expectedPublishedVersionId: string }
  >({
    query: ({ templateId, expectedPublishedVersionId }) => ({
      url: `/admin/v2/messaging/templates/${templateId}/unpublish`,
      method: "POST",
      data: { expectedPublishedVersionId },
    }),
    invalidatesTags: (_result, _error, { templateId }) => [
      { type: "AdminV2MessagingTemplates", id: "LIST" },
      { type: "AdminV2MessagingTemplates", id: templateId },
      { type: "AdminV2AuditLog", id: "LIST" },
    ],
  }),

  archiveAdminV2MessagingTemplate: builder.mutation<
    AdminV2MessageTemplateDetail,
    { templateId: string }
  >({
    query: ({ templateId }) => ({
      url: `/admin/v2/messaging/templates/${templateId}/archive`,
      method: "POST",
      data: {},
    }),
    invalidatesTags: (_result, _error, { templateId }) => [
      { type: "AdminV2MessagingTemplates", id: "LIST" },
      { type: "AdminV2MessagingTemplates", id: templateId },
      { type: "AdminV2AuditLog", id: "LIST" },
    ],
  }),

  restoreAdminV2MessagingTemplate: builder.mutation<
    AdminV2MessageTemplateDetail,
    { templateId: string; expectedDraftVersionId: string }
  >({
    query: ({ templateId, expectedDraftVersionId }) => ({
      url: `/admin/v2/messaging/templates/${templateId}/restore`,
      method: "POST",
      data: { expectedDraftVersionId },
    }),
    invalidatesTags: (_result, _error, { templateId }) => [
      { type: "AdminV2MessagingTemplates", id: "LIST" },
      { type: "AdminV2MessagingTemplates", id: templateId },
      { type: "AdminV2AuditLog", id: "LIST" },
    ],
  }),

  createAdminV2MessagingTemplateImageUpload: builder.mutation<
    AdminV2MessagingTemplateImageUploadResponse,
    AdminV2MessagingTemplateImageUploadInput
  >({
    query: ({ file, registerAsset, altText }) => {
      const formData = new FormData();
      formData.append("image", file);
      if (registerAsset) formData.append("registerAsset", "true");
      if (registerAsset && altText) formData.append("altText", altText);
      return {
        url: "/admin/v2/messaging/templates/images",
        method: "POST",
        data: formData,
      };
    },
    invalidatesTags: [{ type: "AdminV2MessagingTemplates", id: "ASSETS" }],
  }),

  getAdminV2MessagingTemplateAssets: builder.query<
    AdminV2MessagingTemplateAssetListResponse,
    AdminV2MessagingTemplateAssetsQuery | void
  >({
    query: (params) => ({
      url: "/admin/v2/messaging/templates/assets",
      params: optionalQueryParams(params),
    }),
    providesTags: [{ type: "AdminV2MessagingTemplates", id: "ASSETS" }],
    keepUnusedDataFor: 20,
  }),

  updateAdminV2MessagingTemplateAsset: builder.mutation<
    AdminV2MessagingTemplateAsset,
    AdminV2MessagingTemplateAssetMetadataInput
  >({
    query: ({ assetId, expectedVersion, altText, isDecorative }) => ({
      url: `/admin/v2/messaging/templates/assets/${assetId}`,
      method: "PUT",
      data: { expectedVersion, altText, isDecorative },
    }),
    invalidatesTags: [{ type: "AdminV2MessagingTemplates", id: "ASSETS" }],
  }),

  getAdminV2MessagingTemplateVersionHistory: builder.query<
    AdminV2MessagingTemplateVersionHistoryResponse,
    { templateId: string; ownerKey?: string }
  >({
    query: ({ templateId }) => ({
      url: `/admin/v2/messaging/templates/${templateId}/versions`,
    }),
    providesTags: (_result, _error, { templateId }) => [
      { type: "AdminV2MessagingTemplates", id: templateId },
    ],
    keepUnusedDataFor: 20,
  }),

  compareAdminV2MessagingTemplateVersions: builder.query<
    AdminV2MessagingTemplateVersionCompareResponse,
    AdminV2MessagingTemplateVersionCompareInput
  >({
    query: ({ templateId, leftVersionId, rightVersionId }) => ({
      url: `/admin/v2/messaging/templates/${templateId}/versions/compare`,
      params: { leftVersionId, rightVersionId },
    }),
  }),

  restoreAdminV2MessagingTemplateVersionAsDraft: builder.mutation<
    AdminV2MessagingTemplateRestoreAsDraftResponse,
    AdminV2MessagingTemplateRestoreAsDraftInput
  >({
    query: ({ templateId, sourceVersionId, expectedDraftVersionId }) => ({
      url: `/admin/v2/messaging/templates/${templateId}/versions/restore-as-draft`,
      method: "POST",
      data: { sourceVersionId, expectedDraftVersionId },
    }),
    invalidatesTags: (_result, _error, { templateId }) => [
      { type: "AdminV2MessagingTemplates", id: "LIST" },
      { type: "AdminV2MessagingTemplates", id: templateId },
      { type: "AdminV2AuditLog", id: "LIST" },
    ],
  }),

  previewAdminV2MessagingTemplate: builder.mutation<
    AdminV2MessageTemplatePreviewResponse,
    AdminV2MessagingTemplatePreviewInput
  >({
    query: ({ templateId, body }) => ({
      url: `/admin/v2/messaging/templates/${templateId}/preview`,
      method: "POST",
      data: body,
    }),
  }),

  previewAdminV2MessagingNewTemplate: builder.mutation<
    AdminV2MessageTemplatePreviewResponse,
    AdminV2MessagingNewTemplatePreviewInput
  >({
    query: (body) => ({
      url: "/admin/v2/messaging/templates/preview-unsaved",
      method: "POST",
      data: body,
    }),
    transformResponse: (response: {
      preview: AdminV2MessageTemplatePreviewResponse;
    }) => response.preview,
  }),

  previewAdminV2MessagingTemplateHtmlImport: builder.mutation<
    AdminV2MessagingTemplateHtmlImportPreviewResponse,
    AdminV2MessagingTemplateHtmlImportPreviewInput
  >({
    query: (body) => ({
      url: "/admin/v2/messaging/templates/import-html/preview",
      method: "POST",
      data: body,
    }),
  }),

  getAdminV2MessagingTemplateHtmlImportBudget: builder.query<
    AdminV2MessagingTemplateHtmlImportBudget,
    void
  >({
    query: () => ({
      url: "/admin/v2/messaging/templates/import-html/budget",
    }),
    keepUnusedDataFor: 0,
  }),

  previewAdminV2MessagingTemplateHtmlImportFrame: builder.mutation<
    AdminV2MessagingTemplateHtmlImportFramePreviewResponse,
    AdminV2MessagingTemplateHtmlImportFramePreviewInput
  >({
    query: (body) => ({
      url: "/admin/v2/messaging/templates/import-html/frame-preview",
      method: "POST",
      data: body,
    }),
  }),
});
